import { initializeApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  connectAuthEmulator,
  User
} from 'firebase/auth'
import {
  getFirestore,
  connectFirestoreEmulator,
  enableNetwork,
  disableNetwork,
  Firestore
} from 'firebase/firestore'
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable
} from 'firebase/functions'

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
    (import.meta.env.DEV ? 'localhost' : 'app.taxiadministration.se'),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY
const appCheckDebugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN

if (typeof window !== 'undefined' && appCheckDebugToken) {
  // Enable App Check debug mode for preview builds/CI (e.g. GitHub Actions deploy previews)
  ;(self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken
}

if (typeof window !== 'undefined' && recaptchaSiteKey) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true
    })
    console.log('[Firebase] App Check initialized with reCAPTCHA Enterprise')
  } catch (error) {
    console.warn('[Firebase] Failed to initialize App Check:', error)
  }
} else if (typeof window !== 'undefined') {
  console.warn('[Firebase] App Check is not initialized. Missing VITE_RECAPTCHA_SITE_KEY.')
}

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app)

// Connect to emulators in development
const isEmulatorMode = import.meta.env.DEV || import.meta.env.VITE_USE_EMULATORS === 'true'
if (isEmulatorMode) {
  try {
    // Connect Auth emulator immediately (synchronous)
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
    console.log('[Firebase] Connected to Auth emulator')
  } catch (error) {
    console.warn('[Firebase] Auth emulator already connected or failed to connect:', error)
  }
}

// Initialize Firestore and get a reference to the service
// Note: BloomFilter warnings in browser console are informational only
// They suggest creating composite indexes for better query performance
// These warnings don't affect functionality and can be ignored in development

// Enable offline persistence by default to handle connection issues better
let db: Firestore

try {
  db = getFirestore(app)

  // Connect to Firestore emulator in development
  if (isEmulatorMode) {
    try {
      // Connect Firestore emulator immediately (synchronous)
      connectFirestoreEmulator(db, '127.0.0.1', 8080)
      console.log('[Firebase] Connected to Firestore emulator')
    } catch (error) {
      console.warn('[Firebase] Firestore emulator already connected or failed to connect:', error)
    }
  }
} catch (error) {
  console.warn('[Firebase] Error initializing Firestore:', error)
  // Fallback initialization without advanced features
  db = getFirestore(app)
}

export { db }

// Initialize Firebase Functions
export const functions = getFunctions(app, 'europe-west1')

// Connect to Functions emulator in development
if (isEmulatorMode) {
  try {
    connectFunctionsEmulator(functions, '127.0.0.1', 5001)
    console.log('[Firebase] Connected to Functions emulator')
  } catch (error) {
    console.warn('[Firebase] Functions emulator already connected or failed to connect:', error)
  }
}

// Export httpsCallable for easy access
export { httpsCallable }


// Google Auth Provider
const googleProvider = new GoogleAuthProvider()

// Authentication functions
export const authService = {
  // Sign in with email and password
  signInWithEmail: async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password)
      return { user: result.user, error: null }
    } catch (error: any) {
      return { user: null, error: getSwedishErrorMessage(error.code) }
    }
  },

  // Create account with email and password only
  createAccount: async (email: string, password: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password)
      return { user: result.user, error: null }
    } catch (error: any) {
      return { user: null, error: getSwedishErrorMessage(error.code) }
    }
  },

  // Sign in with Google
  signInWithGoogle: async () => {
    try {
      // Try popup first (works in development), fallback to redirect if it fails
      try {
        const result = await signInWithPopup(auth, googleProvider)
        return { user: result.user, error: null }
      } catch (popupError: any) {
        // If popup is blocked or fails, use redirect method
        if (popupError.code === 'auth/popup-blocked' || 
            popupError.code === 'auth/popup-closed-by-user' ||
            popupError.message.includes('Cross-Origin-Opener-Policy')) {
          
          // Use redirect method for production/hosted environments
          await signInWithRedirect(auth, googleProvider)
          // The redirect will handle the rest, returning here is just for TypeScript
          return { user: null, error: null }
        }
        throw popupError
      }
    } catch (error: any) {
      return { user: null, error: getSwedishErrorMessage(error.code) }
    }
  },

  // Handle redirect result (should be called on app initialization)
  handleRedirectResult: async () => {
    try {
      const result = await getRedirectResult(auth)
      if (result) {
        return { user: result.user, error: null }
      }
      return { user: null, error: null }
    } catch (error: any) {
      return { user: null, error: getSwedishErrorMessage(error.code) }
    }
  },

  // Sign out
  signOut: async () => {
    try {
      await signOut(auth)
      return { error: null }
    } catch (error: any) {
      return { error: getSwedishErrorMessage(error.code) }
    }
  },

  // Reset password
  resetPassword: async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email)
      return { error: null }
    } catch (error: any) {
      return { error: getSwedishErrorMessage(error.code) }
    }
  },

  // Get current user
  getCurrentUser: (): User | null => {
    return auth.currentUser
  },

  // Extract company name from display name
  getCompanyName: (user: User | null): string => {
    if (!user?.displayName) return ''
    
    // Extract company name from format "Name (Company)"
    const match = user.displayName.match(/\(([^)]+)\)$/)
    return match ? match[1] : ''
  },

  // Get user display name without company
  getDisplayName: (user: User | null): string => {
    if (!user?.displayName) return user?.email?.split('@')[0] || 'Användare'

    // Remove company name from display name
    const displayName = user.displayName.replace(/\s*\([^)]+\)$/, '')
    return displayName || user.email?.split('@')[0] || 'Användare'
  }
}

// Connection utilities for handling network issues
export const connectionUtils = {
  // Enable Firestore network
  enableFirestoreNetwork: async (): Promise<boolean> => {
    try {
      await enableNetwork(db)
      console.log('[Firebase] Network enabled successfully')
      return true
    } catch (error) {
      console.error('[Firebase] Failed to enable network:', error)
      return false
    }
  },

  // Disable Firestore network
  disableFirestoreNetwork: async (): Promise<boolean> => {
    try {
      await disableNetwork(db)
      console.log('[Firebase] Network disabled successfully')
      return true
    } catch (error) {
      console.error('[Firebase] Failed to disable network:', error)
      return false
    }
  },

  // Reset Firestore connection (disable then enable)
  resetFirestoreConnection: async (): Promise<boolean> => {
    try {
      console.log('[Firebase] Resetting Firestore connection...')
      await disableNetwork(db)
      await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1 second
      await enableNetwork(db)
      console.log('[Firebase] Connection reset successfully')
      return true
    } catch (error) {
      console.error('[Firebase] Failed to reset connection:', error)
      return false
    }
  }
}

// Helper function to convert Firebase error codes to Swedish messages
function getSwedishErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'Användaren finns inte'
    case 'auth/wrong-password':
      return 'Fel lösenord'
    case 'auth/invalid-email':
      return 'Ogiltig e-postadress'
    case 'auth/email-already-in-use':
      return 'E-postadressen används redan'
    case 'auth/weak-password':
      return 'Lösenordet är för svagt (minst 6 tecken)'
    case 'auth/too-many-requests':
      return 'För många försök. Försök igen senare'
    case 'auth/network-request-failed':
      return 'Nätverksfel. Kontrollera din internetanslutning'
    case 'auth/popup-closed-by-user':
      return 'Inloggningen avbröts'
    case 'auth/popup-blocked':
      return 'Popup blockerad av webbläsaren. Försöker omdirigera...'
    case 'auth/cancelled-popup-request':
      return 'Inloggningsförfrågan avbröts'
    case 'auth/user-disabled':
      return 'Kontot har inaktiverats'
    case 'auth/invalid-continue-uri':
      return 'Ogiltig återställningslänk'
    case 'auth/missing-continue-uri':
      return 'Återställningslänk saknas'
    default:
      return 'Ett oväntat fel uppstod. Försök igen'
  }
}

export default app
